import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Lock, Upload, Plus, Trash2, Activity, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { projectApi, formatApiError } from "@/services/projectApi";
import { useTier } from "@/contexts/AuthContext";
import { LiveYieldPanel } from "@/components/yield/LiveYieldWidgets";

/* atoms */
const Lbl = ({ children }) => <label className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">{children}</label>;
const Input = ({ value, onChange, type = "text", step, placeholder, testid, disabled }) => (
    <input type={type} step={step} value={value ?? ""} onChange={onChange} placeholder={placeholder} data-testid={testid} disabled={disabled}
        className="w-full bg-transparent border-0 border-b border-[#d8dcdd] focus:border-[#01696f] disabled:opacity-50 outline-none px-0 py-1.5 text-[13.5px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors" />
);
const Mono = ({ children, className = "" }) => <code className={`tc-mono text-[12px] text-[#0e1416] ${className}`}>{children}</code>;

const Panel = ({ index, title, children, testid }) => (
    <section data-testid={testid} className="border border-[#d8dcdd] bg-[#ffffff] mb-6">
        <header className="flex items-baseline gap-5 px-6 py-5 border-b border-[#d8dcdd]">
            <span className="tc-section-index">{index}</span>
            <h3 className="text-[17px] font-medium tracking-tight text-[#0e1416]">{title}</h3>
        </header>
        <div className="p-6">{children}</div>
    </section>
);

export default function MaturityTab() {
    const { project } = useOutletContext();
    const { tier, meets } = useTier();
    const [searchParams, setSearchParams] = useSearchParams();

    const [mixes, setMixes] = useState(null);
    const [selectedMixId, setSelectedMixId] = useState(searchParams.get("mix") || "");

    useEffect(() => {
        (async () => {
            try {
                const m = await projectApi.listMixes(project.id);
                setMixes(m);
                if (!selectedMixId && m[0]) setSelectedMixId(m[0].id);
            } catch (err) { toast.error(formatApiError(err)); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.id]);

    useEffect(() => {
        if (selectedMixId) setSearchParams((sp) => { sp.set("mix", selectedMixId); return sp; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMixId]);

    if (mixes === null) return <div className="tc-mono text-[11px] text-[#5a6669]">Loading…</div>;

    if (mixes.length === 0) {
        return (
            <div data-testid="maturity-tab-empty" className="border border-[#d8dcdd] bg-[#ffffff] p-8 flex flex-col gap-4">
                <span className="tc-eyebrow text-[#5a6669]">No mixes yet</span>
                <p className="text-[14px] text-[#0e1416] leading-[1.6] max-w-[68ch]">
                    Maturity and strength calibration are configured per mix.
                    Add at least one mix in <strong>Setup → B. Mix definition</strong>, then return here to choose a maturity method and enter calibration points.
                </p>
                <Link to={`/app/projects/${project.id}/setup`} data-testid="maturity-empty-go-setup" className="self-start inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-3.5 py-2 text-[12.5px] transition-colors">
                    Go to Setup <ArrowUpRight size={13} strokeWidth={1.5} />
                </Link>
            </div>
        );
    }

    const mix = mixes.find((m) => m.id === selectedMixId) || mixes[0];

    return (
        <div data-testid="maturity-tab" className="flex flex-col">
            {/* Mix selector */}
            <div className="border border-[#d8dcdd] bg-[#ffffff] mb-6 px-6 py-5">
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <div>
                        <span className="tc-eyebrow text-[#5a6669]">Selected mix</span>
                        <div className="text-[18px] font-medium text-[#0e1416] mt-1">{mix.name}</div>
                        <div className="tc-mono text-[11px] text-[#5a6669]">{mix.binder_type} · w/b {mix.w_b_ratio.toFixed(2)} · aggregate {mix.aggregate_key}</div>
                    </div>
                    {mixes.length > 1 && (
                        <select value={mix.id} onChange={(e) => setSelectedMixId(e.target.value)} data-testid="maturity-mix-selector"
                            className="bg-transparent border border-[#d8dcdd] focus:border-[#01696f] outline-none px-3 py-2 text-[13px] text-[#0e1416]">
                            {mixes.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    )}
                </div>
            </div>

            <Panel1MaturityMethod mix={mix} tier={tier} meets={meets} />
            <Panel2Calibration mix={mix} tier={tier} meets={meets} />
            <PanelCalibrationDiagnostic mix={mix} project={project} tier={tier} meets={meets} />
            <Panel3DualMode mix={mix} project={project} />
            <Panel4History tier={tier} meets={meets} mix={mix} />
        </div>
    );
}

/* ========== Panel 1 — method selector ========== */

function Panel1MaturityMethod({ mix, tier, meets }) {
    const [settings, setSettings] = useState(null);
    const [method, setMethod] = useState("nurse_saul");
    const [T_ref, setTref] = useState(20.0);
    const [E_a, setEa] = useState(33500);
    const [T0, setT0] = useState(-10.0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const s = await projectApi.getMaturity(mix.id);
            if (s) {
                setSettings(s); setMethod(s.method); setTref(s.T_ref); setEa(s.E_a); setT0(s.datum_temp);
            } else {
                setSettings(undefined);
            }
        })();
    }, [mix.id]);

    const canArrhenius = meets("starter");
    const save = async () => {
        setSaving(true);
        try {
            await projectApi.upsertMaturity(mix.id, { method, T_ref, E_a, R: 8.314, datum_temp: T0 });
            toast.success("Maturity settings saved.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Panel index="1" title="Maturity method" testid="maturity-panel-method">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <MethodCard
                    selected={method === "nurse_saul"}
                    onSelect={() => setMethod("nurse_saul")}
                    title="Nurse-Saul"
                    subtitle="Linear · datum-temperature"
                    formula="M(t) = Σ (T(τ) − T₀) · Δτ"
                    unit="Maturity unit · °C·h"
                    testid="method-nurse-saul"
                >
                    <Lbl>Datum temperature T₀ (°C)</Lbl>
                    <Input type="number" step="0.5" value={T0} onChange={(e) => setT0(parseFloat(e.target.value) || 0)} testid="ns-T0-input" />
                    <p className="tc-mono text-[10.5px] text-[#5a6669] mt-2">common range −12 to 0 °C — verify against project guideline</p>
                </MethodCard>

                <MethodCard
                    selected={method === "arrhenius"}
                    onSelect={() => canArrhenius && setMethod("arrhenius")}
                    title="Arrhenius / equivalent age"
                    subtitle="Activation-energy based"
                    formula="t_e(t) = Σ exp[ −(E_a/R) · (1/T(τ) − 1/T_ref) ] · Δτ"
                    unit="Equivalent age unit · hours at T_ref"
                    locked={!canArrhenius}
                    testid="method-arrhenius"
                >
                    {!canArrhenius ? (
                        <div className="flex items-center justify-between">
                            <p className="tc-mono text-[11px] text-[#5a6669]"><Lock size={11} strokeWidth={1.5} className="inline-block mr-1" />Available on Starter and above.</p>
                            <Link to="/pricing" className="tc-mono text-[11px] text-[#01696f] hover:text-[#015a60]">View plans →</Link>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Lbl>E_a (J/mol)</Lbl><Input type="number" step="100" value={E_a} onChange={(e) => setEa(parseFloat(e.target.value) || 0)} testid="arr-Ea-input" /></div>
                                <div><Lbl>T_ref (°C)</Lbl><Input type="number" step="0.5" value={T_ref} onChange={(e) => setTref(parseFloat(e.target.value) || 0)} testid="arr-Tref-input" /></div>
                            </div>
                            <p className="tc-mono text-[10.5px] text-[#5a6669] mt-2">typical OPC 30–40 kJ/mol; blended binders often higher — calibrate per mix · R = 8.314 J/(mol·K)</p>
                        </>
                    )}
                </MethodCard>
            </div>

            <p className="mt-6 tc-mono text-[11px] italic text-[#5a6669] leading-[1.5] max-w-[78ch]">
                The maturity–strength relationship is mix-specific and must be calibrated. Choose the method that matches your project's QA framework — Nurse-Saul is widely accepted for OPC at moderate temperatures; Arrhenius / equivalent age is recommended for blended binders and pours with significant temperature swings.
            </p>

            <div className="mt-5 pt-5 border-t border-[#d8dcdd] flex items-center justify-between">
                <span className="tc-mono text-[11px] text-[#5a6669]">{settings ? `last saved · ${new Date(settings.updated_at).toLocaleString()}` : "not yet configured"}</span>
                <button type="button" onClick={save} disabled={saving} data-testid="maturity-save-method" className="inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-3.5 py-2 text-[12.5px] transition-colors disabled:opacity-60">
                    {saving ? "Saving…" : "Save maturity settings"}
                </button>
            </div>
        </Panel>
    );
}

const MethodCard = ({ selected, onSelect, title, subtitle, formula, unit, children, locked, testid }) => (
    <div data-testid={testid} onClick={onSelect} className={`relative border ${selected ? "border-[#01696f] ring-1 ring-[#01696f]/30" : "border-[#d8dcdd]"} ${locked ? "opacity-80" : "cursor-pointer"} bg-[#f6f7f6] p-5 flex flex-col gap-3 transition-colors`}>
        <div className="flex items-center justify-between">
            <div>
                <div className="flex items-center gap-2">
                    <input type="radio" checked={!!selected} readOnly disabled={locked} className="accent-[#01696f]" />
                    <span className="text-[15px] font-medium text-[#0e1416]">{title}</span>
                </div>
                <span className="tc-mono text-[10.5px] text-[#5a6669]">{subtitle}</span>
            </div>
            {locked && <Lock size={13} strokeWidth={1.5} className="text-[#5a6669]" />}
        </div>
        <div className="bg-[#0e1416] text-[#e6ebec] px-3 py-2 tc-mono text-[12px] tracking-wide">{formula}</div>
        <div className="tc-mono text-[10.5px] text-[#5a6669]">{unit}</div>
        <div className="pt-3 border-t border-[#d8dcdd]">{children}</div>
    </div>
);

/* ========== Panel 2 — strength calibration ========== */

function Panel2Calibration({ mix, tier, meets }) {
    const [points, setPoints] = useState([{ age: "", fc: "" }, { age: "", fc: "" }, { age: "", fc: "" }]);
    const [source, setSource] = useState("lab");
    const [validated, setValidated] = useState(false);
    const [fit, setFit] = useState(null);   // {fc_inf, tau, beta}
    const [curve, setCurve] = useState([]);
    const [saving, setSaving] = useState(false);
    const [existing, setExisting] = useState(null);
    const canField = meets("professional");
    const [method, setMethod] = useState("nurse_saul");

    useEffect(() => {
        (async () => {
            const [c, m] = await Promise.all([
                projectApi.getCalibration(mix.id),
                projectApi.getMaturity(mix.id),
            ]);
            if (c) {
                setExisting(c);
                setPoints((c.points || []).map((p) => ({ age: p.age, fc: p.fc })).concat(Array(Math.max(0, 3 - (c.points || []).length)).fill({ age: "", fc: "" })));
                setSource(c.source); setValidated(c.validated);
                if (c.fit_params?.fc_inf) {
                    setFit(c.fit_params);
                    setCurve(sampleCurve(c.fit_params));
                }
            }
            if (m) setMethod(m.method);
        })();
    }, [mix.id]);

    const validPts = points.map((p) => ({ age: parseFloat(p.age), fc: parseFloat(p.fc) })).filter((p) => p.age > 0 && p.fc > 0);

    const runFit = async () => {
        if (validPts.length < 2) { toast.error("Need at least 2 valid (age, fc) points."); return; }
        try {
            const { fit_params, curve } = await projectApi.fitCalibration(mix.id, validPts);
            setFit(fit_params); setCurve(curve);
            toast.success(`Fit · fc∞=${fit_params.fc_inf.toFixed(1)} τ=${fit_params.tau.toFixed(1)} β=${fit_params.beta.toFixed(2)}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await projectApi.upsertCalibration(mix.id, {
                points: validPts,
                fit_params: fit || {},
                validated,
                source,
            });
            toast.success("Calibration saved.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const ageLabel = method === "nurse_saul" ? "Maturity M (°C·h)" : "Equivalent age t_e (h)";

    return (
        <Panel index="2" title="Strength calibration curve" testid="maturity-panel-calibration">
            <p className="text-[13.5px] text-[#0e1416] leading-[1.6] max-w-[78ch]">
                Provide age + strength pairs from companion cubes/cylinders, or upload a CSV. Concrete Control fits a strength development curve in the chosen maturity domain
                (<span className="tc-mono text-[12px]">{method === "nurse_saul" ? "maturity, °C·h" : "equivalent age, hours"}</span>)
                and uses the same calibrated curve for both predicted and monitored strength estimates.
            </p>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Table */}
                <div className="lg:col-span-7">
                    <div className="flex items-center justify-between mb-2">
                        <span className="tc-eyebrow text-[#5a6669]">Calibration points</span>
                        <button onClick={() => setPoints([...points, { age: "", fc: "" }])} className="tc-mono text-[11px] text-[#01696f] hover:text-[#015a60]">+ add row</button>
                    </div>
                    <div className="grid grid-cols-12 gap-3 tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase">
                        <div className="col-span-5">{ageLabel}</div>
                        <div className="col-span-5">f_c (MPa)</div>
                    </div>
                    {points.map((p, i) => (
                        <div key={i} className="grid grid-cols-12 gap-3 py-1.5 items-center">
                            <div className="col-span-5"><Input type="number" step="0.5" value={p.age} onChange={(e) => setPoints(points.map((x, j) => j === i ? { ...x, age: e.target.value } : x))} placeholder="e.g. 24" testid={`cal-age-${i}`} /></div>
                            <div className="col-span-5"><Input type="number" step="0.1" value={p.fc} onChange={(e) => setPoints(points.map((x, j) => j === i ? { ...x, fc: e.target.value } : x))} placeholder="e.g. 18.5" testid={`cal-fc-${i}`} /></div>
                            <div className="col-span-2 flex justify-end"><button onClick={() => setPoints(points.filter((_, j) => j !== i))} className="p-1 text-[#5a6669] hover:text-[#a13434]"><Trash2 size={12} strokeWidth={1.5} /></button></div>
                        </div>
                    ))}

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button onClick={runFit} data-testid="cal-fit-button" className="inline-flex items-center gap-2 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-3 py-1.5 text-[12px] transition-colors">
                            <Sparkles size={12} strokeWidth={1.5} />Fit curve
                        </button>
                        <label className="inline-flex items-center gap-2 text-[12px] text-[#0e1416] cursor-pointer">
                            <input type="checkbox" checked={validated} onChange={(e) => setValidated(e.target.checked)} className="accent-[#01696f]" />
                            Calibration validated against this project's mix
                        </label>
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <Lbl>Source</Lbl>
                        <label className="inline-flex items-center gap-1.5 text-[12.5px] cursor-pointer">
                            <input type="radio" checked={source === "lab"} onChange={() => setSource("lab")} className="accent-[#01696f]" data-testid="cal-source-lab" /> Lab
                        </label>
                        <label className={`inline-flex items-center gap-1.5 text-[12.5px] ${canField ? "cursor-pointer" : "opacity-60"}`}>
                            <input type="radio" checked={source === "field"} onChange={() => canField && setSource("field")} disabled={!canField} className="accent-[#01696f]" data-testid="cal-source-field" /> Field {!canField && <span className="tc-mono text-[10px] text-[#5a6669]">· Pro</span>}
                        </label>
                    </div>
                </div>

                {/* Chart */}
                <div className="lg:col-span-5">
                    <div className="border border-[#d8dcdd] bg-[#f6f7f6] p-4">
                        <span className="tc-eyebrow text-[#5a6669]">Live preview</span>
                        <CalibrationChart points={validPts} curve={curve} />
                        {fit && (
                            <div className="mt-2 grid grid-cols-3 gap-2 tc-mono text-[11px] text-[#5a6669]">
                                <div>fc∞ = <Mono>{fit.fc_inf?.toFixed(1)}</Mono> MPa</div>
                                <div>τ = <Mono>{fit.tau?.toFixed(1)}</Mono></div>
                                <div>β = <Mono>{fit.beta?.toFixed(2)}</Mono></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-6 pt-5 border-t border-[#d8dcdd] flex items-center justify-between">
                <span className="tc-mono text-[11px] text-[#5a6669]">{existing ? `last saved · ${new Date(existing.updated_at).toLocaleString()}` : "not yet saved"}</span>
                <button type="button" onClick={save} disabled={saving} data-testid="cal-save" className="inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-3.5 py-2 text-[12.5px] transition-colors disabled:opacity-60">
                    {saving ? "Saving…" : "Save calibration"}
                </button>
            </div>
        </Panel>
    );
}

const sampleCurve = (fit) => {
    if (!fit?.fc_inf || !fit?.tau) return [];
    const out = [];
    for (let i = 0; i < 60; i++) {
        const t = Math.pow(10, 0 + (i / 59) * 3.5); // 1 → ~3160
        out.push({ age: t, fc: fit.fc_inf * Math.exp(-Math.pow(fit.tau / t, fit.beta)) });
    }
    return out;
};

function CalibrationChart({ points, curve }) {
    // Linear y, log x. Use SVG inline.
    const W = 360, H = 200, pad = { l: 36, r: 14, t: 12, b: 28 };
    const xs = [...curve.map((p) => p.age), ...points.map((p) => p.age), 1, 1000];
    const ys = [...curve.map((p) => p.fc), ...points.map((p) => p.fc), 5, 50];
    const xMin = 1, xMax = Math.max(...xs);
    const yMin = 0, yMax = Math.max(...ys) * 1.1 || 50;
    const xS = (v) => pad.l + (Math.log10(Math.max(v, 1)) / Math.log10(xMax)) * (W - pad.l - pad.r);
    const yS = (v) => H - pad.b - ((v - yMin) / (yMax - yMin)) * (H - pad.t - pad.b);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" data-testid="calibration-chart" className="mt-3">
            {/* axes */}
            <line x1={pad.l} y1={H - pad.b} x2={W - pad.r} y2={H - pad.b} stroke="#0e1416" strokeWidth="0.7" />
            <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke="#0e1416" strokeWidth="0.7" />
            {/* y ticks */}
            {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                const v = yMin + (yMax - yMin) * f;
                return (
                    <g key={i}>
                        <line x1={pad.l - 3} y1={yS(v)} x2={pad.l} y2={yS(v)} stroke="#5a6669" strokeWidth="0.5" />
                        <text x={pad.l - 6} y={yS(v) + 3} textAnchor="end" fontSize="8" fill="#5a6669" fontFamily="JetBrains Mono">{v.toFixed(0)}</text>
                    </g>
                );
            })}
            {/* x ticks (log) */}
            {[1, 10, 100, 1000].filter((v) => v <= xMax).map((v) => (
                <g key={v}>
                    <line x1={xS(v)} y1={H - pad.b} x2={xS(v)} y2={H - pad.b + 3} stroke="#5a6669" strokeWidth="0.5" />
                    <text x={xS(v)} y={H - pad.b + 12} textAnchor="middle" fontSize="8" fill="#5a6669" fontFamily="JetBrains Mono">{v}</text>
                </g>
            ))}
            {/* curve */}
            {curve.length > 1 && (
                <path d={`M ${curve.map((p) => `${xS(p.age)} ${yS(p.fc)}`).join(" L ")}`} fill="none" stroke="#01696f" strokeWidth="1.4" />
            )}
            {/* points */}
            {points.map((p, i) => (
                <circle key={i} cx={xS(p.age)} cy={yS(p.fc)} r="3" fill="#ffffff" stroke="#0e1416" strokeWidth="0.9" />
            ))}
        </svg>
    );
}

/* ========== Panel 3 — predictive vs monitored (auto-light-up) ========== */

function Panel3DualMode({ mix, project }) {
    const [predicted, setPredicted] = useState(null); // {job, pour}
    const [monitored, setMonitored] = useState(null); // {estimate, pour, n_records}

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const pours = await projectApi.listPours(project.id);
                const mixPours = pours.filter((p) => p.mix_id === mix.id);
                if (!mixPours.length) return;

                // Latest predicted SolverJob across this mix's pours
                let latestJob = null;
                let latestPourForJob = null;
                for (const p of mixPours) {
                    const jobs = await projectApi.listJobs({ project_id: project.id, pour_id: p.id });
                    const succ = jobs.find((j) => j.status === "succeeded");
                    if (succ && (!latestJob || (succ.finished_at || succ.created_at) > (latestJob.finished_at || latestJob.created_at))) {
                        latestJob = succ;
                        latestPourForJob = p;
                    }
                }
                // Latest monitored StrengthEstimate across this mix's pours
                let latestEst = null; let latestPourForEst = null; let latestRecCount = 0;
                for (const p of mixPours) {
                    const ests = await projectApi.listStrengthEstimates(p.id, "monitored");
                    const e = ests[0];
                    if (e && (!latestEst || e.computed_at > latestEst.computed_at)) {
                        latestEst = e; latestPourForEst = p;
                        latestRecCount = (e.inputs_snapshot || {}).n_records || 0;
                    }
                }
                if (cancelled) return;
                if (latestJob) setPredicted({ job: latestJob, pour: latestPourForJob });
                if (latestEst) setMonitored({ estimate: latestEst, pour: latestPourForEst, n_records: latestRecCount });
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [project.id, mix.id]);

    const predicted_active = !!predicted;
    const monitored_active = !!monitored;
    return (
        <Panel index="3" title="Data source · predicted vs monitored" testid="maturity-panel-dual">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`border ${predicted_active ? "border-[#01696f] bg-[#ffffff]" : "border-dashed border-[#d8dcdd] bg-[#f6f7f6]"} p-5 flex flex-col gap-3`} data-testid="dual-card-predicted">
                    <div className="flex items-center gap-2">
                        <ArrowUpRight size={14} strokeWidth={1.5} className={predicted_active ? "text-[#01696f]" : "text-[#5a6669]"} />
                        <span className="text-[14px] font-medium text-[#0e1416]">Predicted temperature history</span>
                        {predicted_active && <span className="tc-mono text-[10px] text-[#01696f] tracking-widest ml-auto">ACTIVE</span>}
                    </div>
                    {predicted_active ? (
                        <>
                            <p className="tc-mono text-[11.5px] text-[#0e1416]">Latest prediction · Pour #{predicted.pour.sequence_index} {predicted.pour.name} · {new Date(predicted.job.finished_at || predicted.job.created_at).toLocaleString()}</p>
                            <Link to={`/app/projects/${project.id}/thermal?pour=${predicted.pour.id}&result=${predicted.job.result_id}`} data-testid="dual-card-predicted-link"
                                  className="self-start tc-mono text-[11.5px] text-[#01696f] hover:text-[#015a60]">View in Thermal tab →</Link>
                        </>
                    ) : (
                        <p className="text-[12.5px] text-[#5a6669] leading-[1.55]">Awaiting solver run — go to <strong>Thermal</strong> tab to run the prediction.</p>
                    )}
                </div>
                <div className={`border ${monitored_active ? "border-[#01696f] bg-[#ffffff]" : "border-dashed border-[#d8dcdd] bg-[#f6f7f6]"} p-5 flex flex-col gap-3`} data-testid="dual-card-monitored">
                    <div className="flex items-center gap-2">
                        <Activity size={14} strokeWidth={1.5} className={monitored_active ? "text-[#01696f]" : "text-[#5a6669]"} />
                        <span className="text-[14px] font-medium text-[#0e1416]">Monitored temperature history</span>
                        {monitored_active && <span className="tc-mono text-[10px] text-[#01696f] tracking-widest ml-auto">ACTIVE</span>}
                    </div>
                    {monitored_active ? (
                        <>
                            <p className="tc-mono text-[11.5px] text-[#0e1416]">Latest monitoring · Pour #{monitored.pour.sequence_index} {monitored.pour.name} · {monitored.n_records} record(s) · {new Date(monitored.estimate.computed_at).toLocaleString()}</p>
                            <Link to={`/app/projects/${project.id}/thermal?pour=${monitored.pour.id}&overlay=monitoring`} data-testid="dual-card-monitored-link"
                                  className="self-start tc-mono text-[11.5px] text-[#01696f] hover:text-[#015a60]">View overlay in Thermal tab →</Link>
                            <LiveYieldPanel
                                pour={monitored.pour}
                                monitoredEstimate={monitored.estimate}
                                projectId={project.id}
                            />
                        </>
                    ) : (
                        <p className="text-[12.5px] text-[#5a6669] leading-[1.55]">Awaiting monitoring data — CSV upload (Starter+) or live API feed (Professional).</p>
                    )}
                </div>
            </div>
            <div className="bg-[#0e1416] text-[#e6ebec] px-5 py-4 mt-4">
                <div className="tc-eyebrow text-[#01696f]">Same maturity engine · same calibration curve</div>
                <p className="mt-2 text-[13px] text-[#e6ebec] leading-[1.55] max-w-[78ch]">
                    Whether the temperature-time history comes from the pre-pour solver or from on-site sensors during curing, Concrete Control applies the maturity method and mix-specific calibration you configured above to estimate in-place strength. One engine, two input sources.
                </p>
            </div>
        </Panel>
    );
}

/* ========== Panel 4 — calibration history teaser ========== */

function CalibrationHistoryTimeline({ mix }) {
    const [revs, setRevs] = useState([]);
    useEffect(() => {
        if (!mix?.id) return;
        let cancelled = false;
        const tick = async () => {
            try { const data = await projectApi.listRevisions(mix.id); if (!cancelled) setRevs(data); } catch { /* ignore */ }
        };
        tick();
        const h = setInterval(tick, 4000);
        return () => { cancelled = true; clearInterval(h); };
    }, [mix?.id]);
    const activate = async (rid) => {
        try { await projectApi.activateRevision(mix.id, rid); toast.success("Revision activated."); setRevs(await projectApi.listRevisions(mix.id)); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div data-testid="calibration-history-timeline" className="border-l border-[#d8dcdd] pl-5 space-y-5">
            {[...revs].reverse().map((r, idx) => (
                <div key={r.id} data-testid={`revision-entry-${r.id}`} className="relative">
                    <span className="absolute -left-[27px] top-1 w-2.5 h-2.5 rounded-full" style={{ background: r.is_active ? "#01696f" : "#d8dcdd" }} />
                    <div className="flex flex-wrap items-baseline gap-2 tc-mono text-[11px] text-[#5a6669]">
                        <span className="text-[#0e1416]">revision {revs.length - idx}</span>
                        <span className={`px-1.5 py-0.5 text-[9.5px] tracking-widest uppercase ${r.source === "lab" ? "bg-[#f6f7f6] text-[#5a6669] border border-[#d8dcdd]" : "bg-[rgba(1,105,111,0.08)] text-[#01696f] border border-[#01696f]"}`}>{r.source === "lab" && r.previous_revision_id == null ? "ROOT · LAB" : r.source.toUpperCase()}</span>
                        <span>· {new Date(r.created_at).toLocaleString()}</span>
                        {r.is_active && <span className="ml-1 px-1.5 py-0.5 text-[9.5px] tracking-widest uppercase bg-[#01696f] text-white">ACTIVE</span>}
                    </div>
                    <div className="tc-mono text-[12px] text-[#0e1416] mt-1">{r.method} · {Object.entries(r.params || {}).map(([k, v]) => `${k}=${Number(v).toFixed(3)}`).join(" · ")}</div>
                    {r.driven_by && (
                        <div className="tc-mono text-[10.5px] text-[#5a6669] mt-1" data-testid={`revision-provenance-${r.id}`}>
                            driven by Pour {(r.driven_by.pour_id || "").slice(0,8)} · {Array.from(new Set(r.driven_by.probe_labels || [])).length} probe(s) ({Array.from(new Set(r.driven_by.probe_labels || [])).join(", ")}) · diagnostic {(r.driven_by.diagnostic_id || "").slice(0,8)}
                        </div>
                    )}
                    {r.notes && <div className="text-[11.5px] text-[#5a6669] mt-1">{r.notes}</div>}
                    {!r.is_active && (
                        <button onClick={() => { if (window.confirm("Activate this revision?")) activate(r.id); }} data-testid={`revision-activate-${r.id}`}
                                className="mt-2 tc-mono text-[10.5px] border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] px-2 py-1 transition-colors">Activate this revision</button>
                    )}
                </div>
            ))}
            {!revs.length && <p className="tc-mono text-[11px] text-[#5a6669]">No revisions yet — the lab-root revision will appear automatically once maturity settings are saved for this mix.</p>}
        </div>
    );
}

function Panel4History({ tier, meets, mix }) {
    const isPro = meets("professional");
    return (
        <Panel index="4" title="Calibration history & next-pour feedback" testid="maturity-panel-history">
            {!isPro ? (
                <div data-testid="history-teaser-locked" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2 tc-mono text-[10.5px] text-[#5a6669] tracking-widest uppercase"><Lock size={12} strokeWidth={1.5} />Professional tier</div>
                    <p className="text-[13px] text-[#0e1416] leading-[1.6] max-w-[78ch]">
                        After each monitored pour, Concrete Control compares predicted vs measured temperature and maturity. Significant deltas suggest updates to <Mono>E_a</Mono> (Arrhenius) or <Mono>T₀</Mono> (Nurse-Saul) for the next pour in the sequence. Field-calibrated curves replace lab-only curves with traceable provenance.
                    </p>
                    <Link to="/pricing" data-testid="history-view-plans" className="self-start inline-flex items-center gap-2 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-3 py-1.5 text-[12px] transition-colors">View plans <ArrowUpRight size={12} strokeWidth={1.5} /></Link>
                </div>
            ) : (
                <CalibrationHistoryTimeline mix={mix} />
            )}
        </Panel>
    );
}

/* ========== Panel X · Calibration diagnostic (Pro-gated) ========== */

function PanelCalibrationDiagnostic({ mix, project, tier, meets }) {
    const isPro = meets("professional");
    const [eligiblePours, setEligiblePours] = useState([]);
    const [selectedPour, setSelectedPour] = useState("");
    const [running, setRunning] = useState(false);
    const [diag, setDiag] = useState(null);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (!isPro || !mix?.id) return;
        (async () => {
            const pours = await projectApi.listPours(project.id);
            const mixPours = pours.filter((p) => p.mix_id === mix.id);
            const eligible = [];
            for (const p of mixPours) {
                const jobs = await projectApi.listJobs({ project_id: project.id, pour_id: p.id });
                if (!jobs.some((j) => j.status === "succeeded")) continue;
                const mon = await projectApi.getMonitoring(p.id);
                const hasCore = (mon.by_probe || []).some((b) => b.probe?.location_hint === "core" && (b.records || []).some((r) => (r.samples || []).length > 0));
                if (hasCore) eligible.push(p);
            }
            setEligiblePours(eligible);
            if (eligible.length && !selectedPour) setSelectedPour(eligible[0].id);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mix?.id, project?.id, isPro]);

    const runDiag = async () => {
        if (!selectedPour) return;
        setRunning(true); setDiag(null); setConfirming(false);
        try { setDiag(await projectApi.runDiagnostic(selectedPour)); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setRunning(false); }
    };

    const apply = async () => {
        try {
            const rev = await projectApi.applyDiagnostic(mix.id, { diagnostic_id: diag.id, notes: "Applied from MaturityTab diagnostic" });
            toast.success(`Field-informed revision ${rev.id.slice(0,8)} is now active.`);
            setConfirming(false); setDiag({ ...diag, applied_revision_id: rev.id });
            // Reload current params reflected in mix's MaturitySettings via the parent page on next mount
        } catch (e) { toast.error(formatApiError(e)); setConfirming(false); }
    };

    return (
        <Panel index="X" title="Calibration diagnostic · field-informed E_a / T₀ for the next pour" testid="maturity-panel-diagnostic">
            {!isPro ? (
                <div data-testid="diagnostic-teaser-locked" className="border border-[#d8dcdd] bg-[#f6f7f6] p-5">
                    <div className="flex items-center gap-2 tc-mono text-[10.5px] text-[#5a6669] tracking-widest uppercase mb-2"><Lock size={12} strokeWidth={1.5} />Professional tier</div>
                    <p className="text-[13px] text-[#0e1416] leading-[1.55]">Field-informed maturity calibration is available on the Professional plan. Compare predicted vs monitored maturity on a completed pour; Concrete Control suggests an updated E_a (Arrhenius) or T₀ (Nurse-Saul) for the next pour in the sequence — the lab calibration remains the reference basis.</p>
                </div>
            ) : (
                <>
                    <p className="tc-mono italic text-[12px] text-[#5a6669] leading-[1.6] mb-3">Compare predicted and monitored maturity for a completed pour. Concrete Control suggests an updated maturity parameter that you can apply to the next pour in the sequence. The original lab calibration remains the reference basis — field-informed revisions build on it with full provenance.</p>
                    <p className="tc-mono text-[11px] text-[#01696f] leading-[1.5] mb-4" data-testid="diagnostic-dual-pillar-note">A field-informed maturity parameter improves predicted in-place strength AND the maturity check on the next pour's thermal-control plan.</p>
                    <div className="flex items-end gap-3 flex-wrap">
                        <label className="flex flex-col gap-1">
                            <span className="tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">Eligible pour (success + core monitoring)</span>
                            <select data-testid="diagnostic-pour-select" value={selectedPour} onChange={(e) => setSelectedPour(e.target.value)} disabled={!eligiblePours.length}
                                    className="bg-transparent border border-[#d8dcdd] text-[13px] px-2 py-1.5 min-w-[260px]">
                                {eligiblePours.length === 0 && <option value="">No eligible pours</option>}
                                {eligiblePours.map((p) => <option key={p.id} value={p.id}>#{p.sequence_index} {p.name}</option>)}
                            </select>
                        </label>
                        <button data-testid="diagnostic-run-button" onClick={runDiag} disabled={!selectedPour || running}
                                className="border border-[#01696f] bg-[#01696f] text-white hover:bg-[#015a60] px-4 py-2 tc-mono text-[12px] disabled:opacity-60">
                            {running ? "Running..." : "Run diagnostic"}
                        </button>
                    </div>

                    {diag && (
                        <div data-testid="diagnostic-result-card" className="mt-5 border border-[#d8dcdd] bg-[#ffffff]">
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#d8dcdd]">
                                <div className="p-4">
                                    <div className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669] mb-2">Current (active revision)</div>
                                    <div className="tc-mono text-[13px] text-[#0e1416]">method: {diag.method}</div>
                                    {Object.entries(diag.current_params || {}).map(([k, v]) => (
                                        <div key={k} className="tc-mono text-[13px] text-[#0e1416]">{k}: {Number(v).toFixed(3)}</div>
                                    ))}
                                </div>
                                <div className="p-4">
                                    <div className="tc-mono text-[10px] tracking-widest uppercase text-[#01696f] mb-2">Suggested (field-informed)</div>
                                    <div className="tc-mono text-[13px] text-[#0e1416]">method: {diag.method}</div>
                                    {Object.entries(diag.suggested_params || {}).map(([k, v]) => (
                                        <div key={k} className="tc-mono text-[13px] text-[#01696f] font-medium">{k}: {Number(v).toFixed(3)}</div>
                                    ))}
                                    <div className="tc-mono text-[11.5px] mt-2 text-[#0e1416]" data-testid="diagnostic-delta">Δ {diag.delta.param_name} = {diag.delta.abs >= 0 ? "+" : ""}{diag.delta.abs}{diag.delta.pct != null ? ` (${diag.delta.pct >= 0 ? "+" : ""}${diag.delta.pct}%)` : ""}</div>
                                </div>
                            </div>
                            <div className="border-t border-[#d8dcdd] p-4 space-y-1.5">
                                <div className="tc-mono text-[11px] text-[#0e1416]" data-testid="diagnostic-confidence">confidence: <span className="text-[#01696f] uppercase">{diag.confidence}</span> · rmse tₑ: {diag.rmse_te} h · rmse T: {diag.rmse_T} °C · n: {diag.n_samples} · coverage: {diag.sample_coverage_h} h</div>
                                <div className="tc-mono text-[10.5px] text-[#5a6669]" data-testid="diagnostic-provenance">based on Pour {(eligiblePours.find((p) => p.id === diag.pour_id) || {}).name || diag.pour_id.slice(0,8)} · {Array.from(new Set(diag.probe_labels || [])).length} probe(s) ({Array.from(new Set(diag.probe_labels || [])).join(", ")}) · sources: {(diag.monitoring_sources || []).join("+")} · computed {new Date(diag.computed_at).toLocaleString()}</div>
                                <p className="tc-mono italic text-[11px] text-[#5a6669]" data-testid="diagnostic-assumption-note">
                                    Decision support — review before applying. The lab calibration remains the reference basis and is preserved in the revision chain.{diag.confidence === "low" ? " Low confidence — consider extending monitoring coverage before applying." : ""}
                                </p>
                            </div>
                            <div className="border-t border-[#d8dcdd] p-4 flex flex-wrap items-center gap-3">
                                {diag.applied_revision_id ? (
                                    <span className="tc-mono text-[11px] text-[#01696f]" data-testid="diagnostic-applied">✓ Applied as revision {diag.applied_revision_id.slice(0,8)} — now active.</span>
                                ) : !confirming ? (
                                    <button data-testid="diagnostic-apply-button" onClick={() => setConfirming(true)} className="border border-[#01696f] text-[#01696f] hover:bg-[#01696f] hover:text-white px-4 py-2 tc-mono text-[12px] transition-colors">Apply to next pour in sequence</button>
                                ) : (
                                    <div data-testid="diagnostic-confirm-strip" className="flex flex-wrap items-center gap-3 w-full bg-[rgba(1,105,111,0.05)] border border-[#01696f] px-3 py-2">
                                        <span className="tc-mono text-[11.5px] text-[#0e1416]">Create a new field-informed revision and set it active for subsequent pours? Existing data and past predictions are not modified.</span>
                                        <button data-testid="diagnostic-confirm-yes" onClick={apply} className="tc-mono text-[11px] bg-[#01696f] text-white hover:bg-[#015a60] px-3 py-1.5">Confirm</button>
                                        <button data-testid="diagnostic-confirm-no" onClick={() => setConfirming(false)} className="tc-mono text-[11px] border border-[#d8dcdd] hover:border-[#5a6669] px-3 py-1.5">Cancel</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </Panel>
    );

}
